/**
 * BMD Input Panel Component
 * Provides an input interface for custom Bending Moment Diagram data
 * Supports x%,y format where x is position percentage and y is moment magnitude
 */
import React, { useState, useCallback, useEffect } from 'react';
import { useUIStore } from '../../store/useUIStore';

interface BMDInputPanelProps {
  beamId: string;
  position: { x: number; y: number };
  onClose: () => void;
}

interface BMDPoint {
  position: number; // 0 to 1 (percentage along beam)
  moment: number; // moment value in kN·m
}

export const BMDInputPanel: React.FC<BMDInputPanelProps> = ({
  beamId,
  position,
  onClose,
}) => {
  const uiStore = useUIStore();
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [previewPoints, setPreviewPoints] = useState<BMDPoint[]>([]);

  // Load existing BMD data if available
  useEffect(() => {
    const existingData = uiStore.customBMDData[beamId];
    if (existingData && existingData.length > 0) {
      const inputString = existingData
        .map(point => `${(point.position * 100).toFixed(1)}%,${point.moment}`)
        .join(' # ');
      setInputValue(inputString);
      setPreviewPoints(existingData);
    }
  }, [beamId, uiStore.customBMDData]);

  // Parse input string to BMD points
  const parseInput = useCallback((input: string): BMDPoint[] | null => {
    try {
      if (!input.trim()) {
        return [];
      }

      const segments = input.split('#').map(s => s.trim());
      const points: BMDPoint[] = [];

      for (const segment of segments) {
        if (!segment) continue;

        // Accept numbers like 0, 100, 12.5, .5, -3.2 etc.
        const match = segment.match(/^([0-9]*\.?[0-9]+)%\s*,\s*(-?[0-9]*\.?[0-9]+)$/);
        if (!match) {
          throw new Error(`Invalid format: "${segment}". Expected format: "x%,y" (x and y may be decimals)`);
        }

        const percentage = parseFloat(match[1]);
        const moment = parseFloat(match[2]);

        if (percentage < 0 || percentage > 100) {
          throw new Error(`Position ${percentage}% must be between 0% and 100%`);
        }

        points.push({
          position: percentage / 100, // Convert to 0-1 range
          moment: moment
        });
      }

      // Sort points by position
      points.sort((a, b) => a.position - b.position);

      return points;
    } catch (err) {
      throw err;
    }
  }, []);

  // Handle input change with real-time validation
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setInputValue(value);

    try {
      const points = parseInput(value);
      if (points) {
        setPreviewPoints(points);
        setError(null);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid input format');
      setPreviewPoints([]);
    }
  }, [parseInput]);

  // Apply BMD data
  const handleApply = useCallback(() => {
    try {
      const points = parseInput(inputValue);
      if (points && points.length > 0) {
        uiStore.setCustomBMDData(beamId, points);
        onClose();
      } else {
        setError('Please enter at least one BMD point');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Invalid input format');
    }
  }, [inputValue, parseInput, uiStore, beamId, onClose]);

  // Clear BMD data
  const handleClear = useCallback(() => {
    uiStore.setCustomBMDData(beamId, []);
    uiStore.setBeamBMDVisibility(beamId, false);
    setInputValue('');
    setPreviewPoints([]);
    setError(null);
  }, [uiStore, beamId]);

  return (
    <div
      className="fixed bg-white border border-gray-300 rounded-lg shadow-xl p-4 z-50 min-w-[400px] max-w-[500px]"
      style={{
        left: position.x,
        top: position.y,
        transform: 'translate(-50%, 0)', // Center horizontally on cursor
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <span className="text-blue-600">📊</span>
          Configure BMD
        </h3>
        <button
          onClick={onClose}
          className="text-gray-400 hover:text-gray-600 text-xl leading-none"
          title="Close"
        >
          ×
        </button>
      </div>

      {/* Beam Info */}
      <div className="mb-4 p-2 bg-gray-50 rounded text-sm text-gray-900">
        <strong>Beam:</strong> {beamId}
      </div>

      {/* Input Instructions */}
      <div className="mb-3 text-sm text-gray-600">
        <p className="font-medium mb-1">Input Format:</p>
        <p className="mb-1"><code>x%,y # x%,y # x%,y</code></p>
        <p className="text-xs">
          Where <code>x%</code> is position along beam (0% to 100%) and <code>y</code> is moment magnitude.
          Both <code>x</code> and <code>y</code> may be decimal values (for example <code>12.5%, 3.2</code> or <code>.5%, -2.75</code>).
          Use <code>#</code> to separate multiple points.
        </p>
      </div>

      {/* Input Area */}
      <div className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">
          BMD Data Points
        </label>
        <textarea
          value={inputValue}
          onChange={handleInputChange}
          placeholder="Example: 0%,0 # 25.5%,15.2 # 50%,30 # 75%,15 # 100%,0"
          className="w-full h-20 p-3 border border-gray-300 rounded-md resize-none text-sm font-mono text-gray-900"
        />
        
        {/* Error Display */}
        {error && (
          <div className="mt-2 p-2 bg-red-50 border border-red-200 rounded text-red-700 text-sm">
            {error}
          </div>
        )}
      </div>

      {/* Preview */}
      {previewPoints.length > 0 && !error && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
          <h4 className="font-medium text-blue-900 mb-2">Preview ({previewPoints.length} points):</h4>
          <div className="text-sm text-blue-800 max-h-20 overflow-y-auto">
            {previewPoints.map((point, index) => (
              <div key={index} className="flex justify-between">
                <span>{(point.position * 100).toFixed(1)}%</span>
                <span>{point.moment} kN·m</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex gap-2 justify-end">
        <button
          onClick={handleClear}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-gray-900"
        >
          Clear BMD
        </button>
        <button
          onClick={onClose}
          className="px-3 py-2 text-sm border border-gray-300 rounded-md hover:bg-gray-50 transition-colors text-gray-900"
        >
          Cancel
        </button>
        <button
          onClick={handleApply}
          disabled={!!error || previewPoints.length === 0}
          className="px-3 py-2 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          Apply BMD
        </button>
      </div>
    </div>
  );
};